import type { DefaultLook, SparkButtonProps } from './types';

type SizeDefinition = Record<SparkButtonProps['size'], string>;
type LookDefinition = Record<DefaultLook | string, string>;
type ShapeSizeDefinition = Record<SparkButtonProps['shape'], SizeDefinition>;

export const BASE_CSS =
  'transition-all font-medium inline-flex items-center justify-center select-none whitespace-nowrap focus-visible:outline-none leading-none';

export const ROUNDED_CSS: ShapeSizeDefinition = {
  rounded: {
    xs: 'rounded-md',
    sm: 'rounded-md',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-lg',
  },
  pill: {
    xs: 'rounded-full',
    sm: 'rounded-full',
    md: 'rounded-full',
    lg: 'rounded-full',
    xl: 'rounded-full',
  },
  circle: {
    xs: 'rounded-full',
    sm: 'rounded-full',
    md: 'rounded-full',
    lg: 'rounded-full',
    xl: 'rounded-full',
  },
} as const;

const padding: SizeDefinition = {
  xs: 'px-2',
  sm: 'px-3',
  md: 'px-4',
  lg: 'px-5',
  xl: 'px-6',
} as const;

export const PADDING_CSS: Record<'rounded' | 'pill', SizeDefinition> = {
  rounded: padding,
  pill: padding,
} as const;

const heightSize: SizeDefinition = {
  xs: 'h-5',
  sm: 'h-6',
  md: 'h-7',
  lg: 'h-10',
  xl: 'h-12',
} as const;

export const HEIGHT_CSS: Record<'rounded' | 'pill', SizeDefinition> = {
  rounded: heightSize,
  pill: heightSize,
} as const;

const textSize: SizeDefinition = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-sm',
  lg: 'text-lg',
  xl: 'text-xl',
};

export const TEXT_SIZE_CSS: Record<'rounded' | 'pill', SizeDefinition> = {
  rounded: textSize,
  pill: textSize,
} as const;

export const WIDTH_HEIGHT_CSS: Record<'circle', SizeDefinition> = {
  circle: {
    xs: 'min-w-[1.25rem] min-h-[1.25rem]',
    sm: 'min-w-[1.75rem] min-h-[1.75rem]',
    md: 'min-w-[2.25rem] min-h-[2.25rem]',
    lg: 'min-w-[3rem] min-h-[3rem]',
    xl: 'min-w-[5rem] min-h-[5rem]',
  },
};

export const ICON_SIZE: SizeDefinition = {
  xs: '12',
  sm: '14',
  md: '16',
  lg: '20',
  xl: '24',
} as const;

export const UNDERLINE_CSS: Record<'text' | string, string> = {
  text: 'underline underline-offset-8 decoration-transparent hover:underline hover:underline-offset-4 hover:decoration-current',
} as const;

// see .radiate-effect CSS in SparkButton.vue
export const RADIATE_EFFECT_CSS: LookDefinition = {
  unfilled: 'radiate-effect',
  filled: `radiate-effect`,
  outlined: `radiate-effect`,
  text: '',
};

export const STACKED_LOOKS = ['stacked-filled', 'stacked-ghost'] as const;
export type StackedLook = (typeof STACKED_LOOKS)[number];

export const STACKED_LAYOUT_CSS = 'group flex-col items-center justify-start';

export const STACKED_ICON_WRAPPER_BASE =
  'inline-flex items-center justify-center rounded-full shrink-0';

export const STACKED_GAP_CSS: SizeDefinition = {
  xs: 'gap-y-1',
  sm: 'gap-y-1',
  md: 'gap-y-1.5',
  lg: 'gap-y-2',
  xl: 'gap-y-2',
} as const;

export const STACKED_ICON_WRAPPER_SIZE_CSS: SizeDefinition = {
  xs: 'w-6 h-6',
  sm: 'w-8 h-8',
  md: 'w-10 h-10',
  lg: 'w-12 h-12',
  xl: 'w-14 h-14',
} as const;

export const STACKED_LABEL_SIZE_CSS: SizeDefinition = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-sm',
  lg: 'text-base',
  xl: 'text-lg',
} as const;

// Fixed label width so every stacked button in a row has the same footprint
// regardless of label length. Multi-word labels wrap when they exceed this
// width; pair with `whitespace-normal` + `break-words` on the label.
export const STACKED_LABEL_WIDTH_CSS: SizeDefinition = {
  xs: 'w-16',
  sm: 'w-20',
  md: 'w-24',
  lg: 'w-32',
  xl: 'w-40',
} as const;
